import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { formatFullDate, getAllGames, getTeamById, getSeasons, stageLabel } from "@/utils/nba";
import TeamBadge from "@/components/base/TeamBadge";

export default function Replays() {
  const [season, setSeason] = useState("all");

  useSeo({
    title: "NBA赛事回放集锦 - NBA直播吧",
    description: "汇总 NBA 已结束比赛的录像回放与集锦，包含常规赛、季后赛及全明星赛的关键事件回顾，支持按赛季筛选。",
    keywords: "NBA回放,NBA录像集锦,NBA比赛回放,NBA集锦,NBA直播吧",
  });

  const seasons = getSeasons();
  const finals = getAllGames()
    .filter((game) => game.status === "final")
    .filter((game) => (season === "all" ? true : game.season === season))
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900 font-medium">回放集锦</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">NBA 赛事回放集锦</h1>
        <p className="mt-2 text-sm text-foreground-600">
          汇总已结束比赛的全场回放与关键事件集锦，点击任意赛事即可进入回放页查看分阶段回顾。
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 rounded-md bg-background-100 p-1">
        <button
          type="button"
          onClick={() => setSeason("all")}
          className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${season === "all" ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-600 hover:bg-background-200"}`}
        >
          全部赛季
        </button>
        {seasons.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setSeason(item)}
            className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${season === item ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-600 hover:bg-background-200"}`}
          >
            {item} 赛季
          </button>
        ))}
      </div>

      {finals.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {finals.map((game) => {
            const home = getTeamById(game.homeTeamId);
            const away = getTeamById(game.awayTeamId);
            if (!home || !away) return null;
            return (
              <Link
                key={game.id}
                to={`/replay/${game.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:border-primary-300 cursor-pointer"
              >
                <div className="flex items-center justify-center gap-4 bg-primary-800 px-5 py-6">
                  <div className="flex flex-col items-center gap-2">
                    <TeamBadge team={away} size="md" />
                    <span className="text-xs text-background-100/80">{away.abbreviation}</span>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold text-background-50">{game.awayScore} - {game.homeScore}</p>
                    <p className="text-[11px] text-background-100/70">{stageLabel(game.stage)}</p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <TeamBadge team={home} size="md" />
                    <span className="text-xs text-background-100/80">{home.abbreviation}</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
                    {away.fullName} vs {home.fullName}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                    <span>{formatFullDate(game.date)}</span>
                    <span className="flex items-center gap-1 text-primary-600 font-medium">
                      <i className="ri-play-circle-line" />
                      观看回放
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-line text-2xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-900">该赛季暂无回放</p>
          <button
            type="button"
            onClick={() => setSeason("all")}
            className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            查看全部回放
          </button>
        </div>
      )}
    </div>
  );
}